import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import Spinner from 'react-bootstrap/Spinner';
import { FormCheck } from 'react-bootstrap';
import Select from 'react-select'
import { selectAll as selectAllStyles } from '../../../redux/slices/styles.slice';
import { selectAll as selectAllTeachers } from '../../../redux/slices/teachers.slice';
import { selectAll as selectAllLevels } from '../../../redux/slices/levels.slice';
import { modalEdit } from '../../../redux/slices/modal.slice';
import { errorEdit, successEdit } from '../../../redux/slices/status.slice';
import { addChosenSticker, stickerUpdateOne, setActiveStickerTab } from '../../../redux/slices/stickers.slice';
import { addChosenEvent, eventUpdateOne } from '../../../redux/slices/events.slice';
import { getLengthOptions, getTimeSlotInterval, naturalCompare, calculateAge } from '../../../utilities';
import { customStylesSelect } from '../customStyleSelect';
import styles from "./index.module.scss";
import './index';
import moment from "moment";
import { timeSlotInterval } from 'constants/variables';
import { studentsService } from '../../../services';
import RadioButton from '../../RadioButton';

const INFO_TAB = 'Info';
const STUDENTS_TAB = 'Students';

function StickerForm(props) {
    const dispatch = useDispatch();
    const { loading, error, success } = useSelector(state => state.status);
    const { chosenSticker } = useSelector(state => state.stickers);
    const { chosenBoard } = useSelector(state => state.boards);
    const { id, isAdmin } = useSelector(state => state.personality.user);
    const { chosenUser } = useSelector(state => state.users);
    const allStyles = useSelector(selectAllStyles);
    const allTeachers = useSelector(selectAllTeachers);
    const allLevels = useSelector(selectAllLevels);
    const [action, setAction] = useState('STICKER_ADD_ONE');
    const [clickDeletItem, setClickDeletItem] = useState(false);
    const [interval, setInterval] = useState(chosenSticker?.length ? getTimeSlotInterval(chosenSticker.length) : timeSlotInterval.minutes_15);
    const [lengthOptions, setLengthOption] = useState([]);
    const [defaultLengthValue, setDefaultLengthValue] = useState('');
    const [attachedStudents, setAttachedStudents] = useState([]);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [activeTab, setActiveTab] = useState(INFO_TAB);
    const handleTabChange = (value) => {
        setActiveTab(value);
        dispatch(setActiveStickerTab(value));
    };
    const [newStudentName, setNewStudentName] = useState('');
    const [newStudentBirthDate, setNewStudentBirthDate] = useState('');
    const [addStudentError, setAddStudentError] = useState(null);
    const [uploadErrors, setUploadErrors] = useState([]);

    const onChangeCheckbox = (props) => {
        setInterval(+props.target.value);
    }

    const objectYup = {
        name: yup.string()
            .required("This field is required")
            .min(2, 'Name is too short - should be 2 chars minimum.'),
        length: yup.lazy(value => {
            switch (typeof value) {
              case 'object':
                return yup.object({
                    value: yup.number().required("This field is required")
                });
              case 'string':
                return yup.string().required("This field is required");
              default:
                return yup.mixed().required("This field is required");
            }
          }),
    };

    const schema = yup.object(objectYup).required();
    const { register, handleSubmit, formState: { errors }, clearErrors: clearFormErrors, reset: resetForm, control, getValues, setValue } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            name: chosenSticker?.className || '',
            isReusable: chosenSticker?.isReusable || false,
        }
    });

    useEffect(() => {
        dispatch(errorEdit(null));
        dispatch(successEdit(null));
        if (chosenSticker?.students) {
            setAttachedStudents(chosenSticker.students || []);
        } else {
            setAttachedStudents([]);
        }
        const userId = isAdmin ? chosenUser?.id : id;
        if (userId) {
            const boardId = chosenSticker?.boardId || chosenBoard?.id;
            if (boardId) {
                dispatch({
                    type: "STUDENTS_GET_ALL",
                    query: { userId, boardId }
                });
            }
        }
        return () => {
            clearFormErrors();
            resetForm();
            dispatch(successEdit(null));
            dispatch(errorEdit(null));
            setAttachedStudents([]);
            setNewStudentName('');
            setNewStudentBirthDate('');
        }
    }, [chosenBoard, isAdmin, chosenUser, id, chosenSticker, dispatch, clearFormErrors, resetForm])

    useEffect(() => {
        clearFormErrors();
        resetForm();
    }, [props.path, clearFormErrors, resetForm]);

    useEffect(() => {
        if (success) resetForm();
    }, [success, resetForm]);

    useEffect(() => {
        return () => {
            setUploadErrors([]);
        };
    }, []);

    
    useEffect(() => {
        if (error) setClickDeletItem(false);
    }, [error])

    

    const getOptions = (data, entity) => {
        return data.map(item => ({
            value: item.id,
            label: entity === 'level' ? item.levelName : item[`${entity}Name`],
            id: item.id,
            color: item.color
        }))
        .sort((a, b) => naturalCompare(a.label, b.label));
    };

    const getDefaultOptionsMulti = (entity) => {
        if (chosenSticker) {
            if (entity === "teachers") {
                let data = []
                chosenSticker[entity]?.map((item) => (
                    data.push({ value: item, label: item })
                ));
                chosenSticker.teachersId?.map((item, index) => (
                    data[index].id = item
                ));
                return data
            }
            if (entity !== "levels") {
                return chosenSticker[entity]?.map((item) => (
                    { value: item, label: item, id: item.id })) || []
            }
            if (entity === "levels") {
                let values = [];
                allLevels.forEach(item => {
                    const isExist = chosenSticker.levelsId.includes(item.id);
                    if (isExist) values.push({value: item.id,label: item.levelName,id: item.id,color: item.color});
                })

                return values;
            }
        } else {
            return []
        }
    }

    const teachersDefaultOptions = getDefaultOptionsMulti("teachers");
    const stylesDefaultOptions = getDefaultOptionsMulti("styles");
    const levelsDefaultOptions = getDefaultOptionsMulti("levels");

    const setLengthOptionData = useCallback((intervalValue) => {
        const [optionsLength, defaultLength] = getLengthOptions(chosenSticker, intervalValue);
        setLengthOption(optionsLength);
        setDefaultLengthValue(defaultLength);
        chosenSticker?.length && setValue('length', defaultLength);
    }, [chosenSticker, setValue])

    useEffect(() => {
        const currentStickerTimeInterval = chosenSticker?.length ? getTimeSlotInterval(chosenSticker.length) : interval;

        if (chosenSticker?.length && currentStickerTimeInterval !== interval) {
            setLengthOptionData(interval);
        } else {
            setLengthOptionData(currentStickerTimeInterval);
        } 
    }, [chosenSticker, interval, setLengthOptionData, setValue]);

    const optionsStyles = getOptions(allStyles, 'style');
    const optionsTeachers = getOptions(allTeachers, 'teacher');
    const optionsLevels = getOptions(allLevels, 'level');

    const buildEventBody = useCallback((formValues, studentsList) => {
        if (!props.isEvent) {
            return {
                className: formValues.name,
                userId: isAdmin ? chosenUser.id : id,
                "length": formValues.length?.value || '',
                "styles": formValues.style?.map(item => item.label) || [],
                "teachers": formValues.teacher?.map(item => item.label) || [],
                "teachersId": formValues.teacher?.map(item => item.id) || [],
                "levels": formValues.level?.map(item => item.color) || [],
                "levelsId": formValues.level?.map(item => item.value) || [],
                "stickerId": chosenSticker?.id || '',
                'isReusable': formValues.isReusable,
                'boardId': chosenBoard?.id || '',
                "studentIds": studentsList.map(student => student.id),
            };
        }

        const eventLengthValue = formValues.length?.value ?? chosenSticker?.length ?? '';
        return {
            stickerId: chosenSticker.id,
            scheduleId: chosenSticker.id,
            id: chosenSticker.id,
            "length": eventLengthValue,
            className: formValues.name ?? chosenSticker.className,
            start: chosenSticker.start,
            end: moment(chosenSticker.start, "HH:mm").add(eventLengthValue, "hours").format("HH:mm"),
            "styles": formValues.style?.map(item => item.label) ?? chosenSticker.styles ?? [],
            "teachers": formValues.teacher?.map(item => item.label) ?? chosenSticker.teachers ?? [],
            "teachersId": formValues.teacher?.map(item => item.id) ?? chosenSticker.teachersId ?? [],
            "levels": formValues.level?.map(item => item.color) ?? chosenSticker.levels ?? [],
            studioId: chosenSticker.studioId,
            prevStudioId: chosenSticker.studioId,
            userId: isAdmin ? chosenUser.id : id,
            dayOfTheWeek: chosenSticker.dayOfTheWeek,
            "levelsId": formValues.level?.map(item => item.value) ?? chosenSticker.levelsId ?? [],
            "studentIds": studentsList.map(student => student.id),
        };
    }, [props.isEvent, chosenSticker, chosenBoard, isAdmin, chosenUser, id]);

    const onSubmit = (value) => {
        dispatch(successEdit(null));
        dispatch(errorEdit(null));

        if ((action === 'STICKER_REMOVE_ONE' || action === 'EVENTS_REMOVE_ONE') && !clickDeletItem) {
            setClickDeletItem(true);
            return
        }
        const body = buildEventBody(value, attachedStudents);
        dispatch({
            type: action,
            payload: {
                body,
                prevEvent: chosenSticker,
                addSticker: false,
                deleteSticker: false,
                fromForm: true,
                attachedStudents,
            }
        });
        resetForm();
    };

    const syncStudentsState = useCallback((updatedStudents) => {
        if (!chosenSticker?.id) return;
        const values = getValues();
        const body = buildEventBody(values, updatedStudents);
        const updatedPayload = {
            ...chosenSticker,
            ...body,
            students: updatedStudents,
        };

        if (props.isEvent) {
            dispatch(eventUpdateOne(updatedPayload));
            dispatch(addChosenEvent(updatedPayload));
        } else {
            // For regular stickers, update Redux state so students persist after closing/opening
            // stickerUpdateOne expects { stickerId, ...changes }
            dispatch(stickerUpdateOne({
                stickerId: chosenSticker.id,
                id: chosenSticker.id,
                ...updatedPayload,
                students: updatedStudents,
            }));
        }
        dispatch(addChosenSticker(updatedPayload));
    }, [chosenSticker, buildEventBody, dispatch, getValues, props.isEvent]);

    const handleDetachStudent = useCallback((student) => {
        if (!chosenSticker?.id) return;
        const updatedStudents = attachedStudents.filter((s) => s.id !== student.id);
        setAttachedStudents(updatedStudents);
        const values = getValues();
        const body = buildEventBody(values, updatedStudents);
        
        if (props.isEvent) {
            dispatch({
                type: "EVENT_STUDENT_DETACH",
                payload: {
                    body,
                    prevEvent: chosenSticker,
                    studentId: student.id,
                    attachedStudents: updatedStudents,
                }
            });
        } else {
            dispatch({
                type: "STICKER_STUDENT_DETACH",
                payload: {
                    body,
                    prevSticker: chosenSticker,
                    studentId: student.id,
                    attachedStudents: updatedStudents,
                }
            });
        }
    }, [chosenSticker, attachedStudents, buildEventBody, dispatch, getValues, props.isEvent]);

    const handleAddStudent = useCallback(() => {
        if (!chosenSticker?.id) return;
        const trimmedName = newStudentName.trim();
        if (!trimmedName) {
            setAddStudentError('Student name is required');
            return;
        }
        let normalizedBirthDate = null;
        if (newStudentBirthDate) {
            const parsedBirthDate = moment(newStudentBirthDate, 'YYYY-MM-DD', true);
            if (!parsedBirthDate.isValid()) {
                setAddStudentError('Birth date is invalid');
                return;
            }
            normalizedBirthDate = parsedBirthDate.format('YYYY-MM-DD');
        }
        // Board must exist before attaching
        const boardIdForStudent = chosenSticker.boardId || chosenBoard?.id;
        if (!boardIdForStudent) {
            setAddStudentError('Board is not selected');
            return;
        }

        setAddStudentError(null);
        const values = getValues();
        const body = buildEventBody(values, attachedStudents);
        
        if (props.isEvent) {
            dispatch({
                type: "EVENT_STUDENT_ATTACH",
                payload: {
                    body: {
                        name: trimmedName,
                        birthDate: normalizedBirthDate,
                        scheduleId: chosenSticker.id,
                        boardId: boardIdForStudent,
                        userId: isAdmin ? chosenUser?.id : id,
                    },
                    prevEvent: chosenSticker,
                    eventBody: body,
                    attachedStudents,
                }
            });
        } else {
            dispatch({
                type: "STICKER_STUDENT_ATTACH",
                payload: {
                    body: {
                        name: trimmedName,
                        birthDate: normalizedBirthDate,
                        stickerId: chosenSticker.id,
                        boardId: boardIdForStudent,
                        userId: isAdmin ? chosenUser?.id : id,
                    },
                    prevSticker: chosenSticker,
                    stickerBody: body,
                    attachedStudents,
                }
            });
        }
        setNewStudentName('');
        setNewStudentBirthDate('');
    }, [chosenSticker, chosenBoard?.id, isAdmin, chosenUser, id, newStudentName, newStudentBirthDate, attachedStudents, buildEventBody, getValues, dispatch, props.isEvent]);

    const currentValues = getValues();
    
    return (<>
        {loading ? (
            <div className={styles.spinnerWrapper}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>) : (
            <>
                {clickDeletItem ? (
                    <div className={styles.confirm}>Are you sure you want to delete this Class? </div>) : null}
                <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
                    {clickDeletItem ? null : (<>
                        <div className={styles.formAnswer}>
                            {error ? <div className="text-danger">{error}</div> : null}
                            {success ? <div className="text-success">{success}</div> : null}
                        </div>
                        {chosenSticker && (
                            <div className={styles.tabsContainer}>
                                <RadioButton
                                    onChangeHandle={handleTabChange}
                                    value={INFO_TAB}
                                    uniqId="sticker-form-info-tab"
                                    activeValue={activeTab}
                                    size="md"
                                >
                                    Info
                                </RadioButton>
                                <RadioButton
                                    onChangeHandle={handleTabChange}
                                    value={STUDENTS_TAB}
                                    uniqId="sticker-form-students-tab"
                                    activeValue={activeTab}
                                    size="md"
                                >
                                    Students {attachedStudents.length > 0 && `(${attachedStudents.length})`}
                                </RadioButton>
                            </div>
                        )}
                        {(!chosenSticker || activeTab === INFO_TAB) && (
                            <>
                        <label>Name</label> <input  {...register("name")} />
                        {errors.name ? (
                            <div className={styles.formAnswer}>
                                <div className="text-danger">{errors.name?.message}</div>
                            </div>) : null}
                        {allLevels.length > 0 ? (<>
                            <label>Level</label>
                            <Controller
                                render={
                                    ({ field: { onChange, onBlur, value, ref } }) => <Select
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            selected={value}
                                            styles={customStylesSelect}
                                            name="level"
                                            options={optionsLevels}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            menuPlacement="top"
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            isMulti
                                        defaultValue={currentValues?.level?.length > 0 ? currentValues.level : levelsDefaultOptions}
                                        />
                                }
                                control={control}
                                name="level"
                                defaultValue={currentValues?.level?.length > 0 ? currentValues.level : levelsDefaultOptions}
                            /></>) : (<div className={styles.add}>
                                <label>No Levels Created</label>
                                <div onClick={() => dispatch(modalEdit('Levels'))}>Add Level +</div>

                            </div>)}

                        {errors.level ? (
                            <div className={styles.formAnswer}>
                                <div className="text-danger">{errors.level?.message}</div>
                            </div>) : null}

                        {allTeachers.length > 0 ? (<><label>Teacher</label>
                            <Controller
                                render={
                                    ({ field: { onChange, onBlur, value, ref } }) => {
                                        return <Select
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            selected={value}
                                            styles={customStylesSelect}
                                            name="teacher"
                                            options={optionsTeachers}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            menuPlacement="top"
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            isMulti
                                            defaultValue={currentValues?.teacher?.length > 0 ? currentValues.teacher : teachersDefaultOptions}
                                        />
                                    }
                                }
                                control={control}
                                name="teacher"
                                defaultValue={currentValues?.teacher?.length > 0 ? currentValues.teacher : teachersDefaultOptions}
                            /></>) : (<div className={styles.add}>
                                <label>No Teachers Created</label>
                                <div onClick={() => dispatch(modalEdit('Teachers'))}>Add Teacher+</div>

                            </div>)}

                        {errors.teacher ? (
                            <div className={styles.formAnswer}>
                                <div className="text-danger">{errors.teacher?.message}</div>
                            </div>) : null}
                        {allStyles.length > 0 ? (<>
                            <label>Style</label>
                            <Controller
                                render={
                                    ({ field: { onChange, onBlur, value, ref } }) => <Select
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        selected={value}
                                        styles={customStylesSelect}
                                        name="styles"
                                        options={optionsStyles}
                                        className="basic-multi-select"
                                        classNamePrefix="select"
                                        menuPlacement="top"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        isMulti
                                        defaultValue={currentValues?.style?.length > 0 ? currentValues.style : stylesDefaultOptions}
                                    />
                                }
                                control={control}
                                name="style"
                                defaultValue={currentValues?.style?.length > 0 ? currentValues.style : stylesDefaultOptions}
                            />
                        </>) : (<div className={styles.add}>
                            <label>No Styles Created</label>
                            <div onClick={() => dispatch(modalEdit('Styles'))}>Add Style +</div>

                        </div>)}

                        {errors.style ? (
                            <div className={styles.formAnswer}>
                                <div className="text-danger">{errors.style?.message}</div>
                            </div>) : null}
                        <label>Length</label>
                        <Controller
                            render={
                                ({ field: { onChange, onBlur, value, ref } }) => <Select
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    selected={value}
                                    value={value || defaultLengthValue}
                                    styles={customStylesSelect}
                                    name="length"
                                    options={lengthOptions}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    menuPlacement="top"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    defaultValue={currentValues?.length?.length > 0 ? currentValues.length : defaultLengthValue}
                                />
                            }
                            control={control}
                            name="length"
                            defaultValue={currentValues?.length?.length > 0 ? currentValues.length : defaultLengthValue}
                        />
                        <>
                            <FormCheck type="radio" label="5 minutes" value={timeSlotInterval.minutes_5} checked={timeSlotInterval.minutes_5 === interval} onChange={onChangeCheckbox} required={false}/>
                            <FormCheck type="radio" label="15 minutes" value={timeSlotInterval.minutes_15} checked={timeSlotInterval.minutes_15 === interval} onChange={onChangeCheckbox} required={false}/>
                        </>
                        {errors.length ? (
                            <div className={styles.formAnswer}>
                                <div className="text-danger">{errors.length?.message}</div>
                            </div>) : null}
                        {!props.isEvent && <div className={styles.checkbox}>
                            <input type="checkbox" {...register("isReusable")} />
                            <label>This class has duplicates</label>
                        </div>
                        }

                        {errors.isReusable ? (
                            <div className={styles[`auth-form__answer`]}>
                                <div className="text-danger">{errors.isReusable.message}</div>
                            </div>) : null}
                            </>  
                        )}

                        {chosenSticker && activeTab === STUDENTS_TAB && (
                            <div className={styles.studentsSection}>
                                <div className={styles.studentsList}>
                                    <div className={styles.uploadSection}>
                                        <input
                                            type="file"
                                            accept=".xls,.xlsx"
                                            id="file-upload"
                                            style={{ display: 'none' }}
                                            onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;
                                                
                                                if (!chosenSticker?.id) {
                                                    dispatch(errorEdit('Please save the class first before uploading students'));
                                                    return;
                                                }

                                                setUploadingFile(true);
                                                setUploadErrors([]);
                                                try {
                                                    const userId = isAdmin ? chosenUser?.id : id;
                                                    // Determine sticker type: schedule sticker has dayOfTheWeek or start field
                                                    const isScheduleSticker = chosenSticker.dayOfTheWeek !== undefined || chosenSticker.start !== undefined;
                                                    const stickerType = isScheduleSticker ? 'schedule' : 'regular';
                                                    const response = await studentsService.uploadFromXLS(
                                                        file,
                                                        chosenSticker.id,
                                                        userId,
                                                        stickerType
                                                    );
                                                    
                                                    // Update students list
                                                    if (response.data?.students) {
                                                        setAttachedStudents(response.data.students);
                                                        syncStudentsState(response.data.students);
                                                    }
                                                    
                                                    dispatch(successEdit(
                                                        `Uploaded successfully! Created: ${response.data.created}, Attached: ${response.data.attached}`
                                                    ));
                                                    
                                                    // Save upload errors if any
                                                    if (response.data.errors && response.data.errors.length > 0) {
                                                        setUploadErrors(response.data.errors);
                                                    }
                                                } catch (error) {
                                                    dispatch(errorEdit(
                                                        error.response?.data?.message || 'Failed to upload students from file'
                                                    ));
                                                    setUploadErrors([]);
                                                } finally {
                                                    setUploadingFile(false);
                                                    e.target.value = ''; // Reset input
                                                }
                                            }}
                                        />
                                        <label htmlFor="file-upload" className={styles.uploadButton}>
                                            {uploadingFile ? 'Uploading...' : 'Upload Students from XLS'}
                                        </label>
                                    </div>
                                    {uploadErrors.length > 0 && (
                                        <div className={styles.uploadErrorsContainer}>
                                            <div className={styles.uploadErrorsHeader}>
                                                <span className="text-danger">Upload Errors ({uploadErrors.length})</span>
                                            </div>
                                            <div className={styles.uploadErrorsScroll}>
                                                {uploadErrors.map((error, index) => (
                                                    <div key={index} className={styles.uploadErrorItem}>
                                                        <span className="text-danger">{error}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <div className={styles.studentsScrollContainer}>
                                        {attachedStudents.length > 0 ? (
                                            [...attachedStudents].sort((a, b) => {
                                                const nameA = (a.name || '').toLowerCase();
                                                const nameB = (b.name || '').toLowerCase();
                                                return nameA.localeCompare(nameB);
                                            }).map((student) => {
                                                const age = student.birthDate ? calculateAge(student.birthDate) : null;
                                                const formattedBirthDate = student.birthDate ? moment(student.birthDate).format('MM/DD/YYYY') : null;
                                                return (
                                                <div key={student.id} className={styles.studentItem}>
                                                    <span>
                                                        {student.name}
                                                        {age !== null ? ` (${age} years)` : ''}
                                                        {formattedBirthDate ? ` • ${formattedBirthDate}` : ''}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className={styles.removeStudentButton}
                                                        onClick={() => handleDetachStudent(student)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            )})
                                        ) : (
                                            <div className={styles.noStudents}>No students attached to this class</div>
                                        )}
                                    </div>
                                </div>
                                <div className={styles.addStudentForm}>
                                    <input
                                        type="text"
                                        placeholder="Student name"
                                        value={newStudentName}
                                        onChange={(e) => {
                                            setNewStudentName(e.target.value);
                                            if (addStudentError) setAddStudentError(null);
                                        }}
                                    />
                                    <input
                                        type="date"
                                        value={newStudentBirthDate}
                                        onChange={(e) => {
                                            setNewStudentBirthDate(e.target.value);
                                            if (addStudentError) setAddStudentError(null);
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className={styles.addStudentButton}
                                        onClick={handleAddStudent}
                                    >
                                        Add
                                    </button>
                                </div>
                                {addStudentError && (
                                    <div className={styles.formAnswer}>
                                        <div className="text-danger">{addStudentError}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>)}
                    {activeTab !== STUDENTS_TAB ? ( <>
                        {!chosenSticker ? <input type="submit" className={styles.submit} value="Save"
                        onClick={() => setAction('STICKER_ADD_ONE')} /> : (
                        props.isEvent ? (
                            <div className={styles.buttons}>
                                {clickDeletItem ? (<>
                                    <button className={styles.submitUpdate}
                                        onClick={() => setClickDeletItem(false)}> No
                                    </button>
                                    <input type="submit" className={styles.submitDelete} value="Yes" />
                                </>) : (<><input type="submit" className={styles.submitDelete} value="Delete"
                                    onClick={() => setAction('EVENTS_REMOVE_ONE')} />
                                    <input type="submit" className={styles.submitUpdate} value="Save"
                                        onClick={() => setAction('EVENTS_UPDATE_ONE')} /></>)}
                            </div>
                        ) : (
                            <div className={styles.buttons}>
                                {clickDeletItem ? (<>
                                    <button className={styles.submitUpdate}
                                        onClick={() => setClickDeletItem(false)}> No
                                    </button>
                                    <input type="submit" className={styles.submitDelete} value="Yes" />
                                </>) : (<><input type="submit" className={styles.submitDelete} value="Delete"
                                    onClick={() => setAction('STICKER_REMOVE_ONE')} />
                                    <input type="submit" className={styles.submitUpdate} value="Save"
                                        onClick={() => setAction('STICKER_UPDATE_ONE')} /></>)}
                            </div>
                        )
                    )}
                    </>) : null}
                </form>
            </>)}
    </>)
}


export default StickerForm;



